import {
  CustomerSegment,
  CustomerSegmentLabels,
  ReleaseChannel,
  ReleaseChannelLabels,
  type UserGroupRecordListPartner,
  UserGroupType,
  type UserRecordMinimal
} from '@navio/nsdk';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NoddiBadge, type NoddiColumnDef } from 'noddi-ui-web';
import {
  CUSTOMER_SEGMENT_ICON_MAP,
  getLabel,
  getLabelMapFromEntries,
  getUserGroupTypeLabel,
  RELEASE_CHANNEL_ICON_MAP,
  USER_GROUP_TYPE_ICON_MAP
} from 'noddi-util';
import { ImpersonationTableHeader } from './ImpersonationTableHeader';

const customerSegmentLabelMap = getLabelMapFromEntries(CustomerSegment, CustomerSegmentLabels);
const releaseChannelLabelMap = getLabelMapFromEntries(ReleaseChannel, ReleaseChannelLabels);

export const getImpersonationUserColumns = (
  userGroupsByUserId: Map<number, UserGroupRecordListPartner[]>
): NoddiColumnDef<UserRecordMinimal>[] => [
  {
    header: () => <ImpersonationTableHeader label='ID' description='Unique user account ID.' />,
    accessorKey: 'id',
    size: 80
  },
  {
    header: () => (
      <ImpersonationTableHeader
        label='Name'
        description='User name with organization and group memberships shown underneath.'
      />
    ),
    accessorKey: 'name',
    cell: ({ row }) => renderUserName(row.original, userGroupsByUserId.get(row.original.id))
  },
  {
    header: () => <ImpersonationTableHeader label='Email' description='Email address used by the user account.' />,
    accessorKey: 'email'
  },
  {
    header: () => (
      <ImpersonationTableHeader label='Phone number' description='Phone number used by the user account.' />
    ),
    accessorKey: 'phoneNumber',
    meta: { type: 'phone' }
  },
  {
    id: 'customerType',
    header: () => (
      <ImpersonationTableHeader label='Customer type' description='Types of customers this user belongs to.' />
    ),
    cell: ({ row }) =>
      renderCustomerContexts(
        userGroupsByUserId.get(row.original.id),
        (group) => getUserGroupTypeLabel(group),
        (group) => USER_GROUP_TYPE_ICON_MAP[group.type]
      )
  },
  {
    id: 'customerSegment',
    header: () => (
      <ImpersonationTableHeader
        label='Customer segment'
        description='Lifecycle and loyalty segments for the user’s customers.'
      />
    ),
    cell: ({ row }) =>
      renderCustomerContexts(
        userGroupsByUserId.get(row.original.id)?.filter((group) => group.segment),
        (group) => getLabel(group.segment!.segment, customerSegmentLabelMap),
        (group) => CUSTOMER_SEGMENT_ICON_MAP[group.segment!.segment]
      )
  },
  {
    id: 'releaseChannel',
    header: () => (
      <ImpersonationTableHeader
        label='Release channel'
        description='Feature release channels assigned to the user’s customers.'
      />
    ),
    cell: ({ row }) =>
      renderCustomerContexts(
        userGroupsByUserId.get(row.original.id),
        (group) => getLabel(group.releaseChannel, releaseChannelLabelMap),
        (group) => RELEASE_CHANNEL_ICON_MAP[group.releaseChannel]
      )
  }
];

const renderUserName = (user: UserRecordMinimal, groups: UserGroupRecordListPartner[] | undefined) => {
  const customerNames = [
    ...new Set(groups?.filter((group) => group.type !== UserGroupType.PERSONAL).map((group) => group.name))
  ];

  return (
    <div className='flex flex-col'>
      <span>{user.name}</span>
      {customerNames.map((name) => (
        <span key={name} className='text-xs text-muted-foreground'>
          {name}
        </span>
      ))}
    </div>
  );
};

const renderCustomerContexts = (
  groups: UserGroupRecordListPartner[] | undefined,
  getLabel: (group: UserGroupRecordListPartner) => string,
  getIcon: (group: UserGroupRecordListPartner) => NoddiIconName
) => {
  const uniqueContexts = new Map(groups?.map((group) => [getLabel(group), group]));

  return (
    <div className='flex flex-wrap gap-2'>
      {[...uniqueContexts.values()].map((group) => (
        <NoddiBadge key={getLabel(group)} variant='outline' className='gap-1'>
          <NoddiIcon name={getIcon(group)} size='small' />
          {getLabel(group)}
        </NoddiBadge>
      ))}
    </div>
  );
};
